import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Hotel, Car, Package, ShieldCheck, Lock, ArrowRight, User, CreditCard } from "lucide-react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ICON = { flight: Plane, hotel: Hotel, car: Car, package: Package };

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthed, user } = useAuth();
  const { convert, code } = useCurrency();
  const [item, setItem] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", dob: "", nationality: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const incoming = location.state?.item;
    if (incoming) { sessionStorage.setItem("pendingBooking", JSON.stringify(incoming)); setItem(incoming); }
    else { const s = sessionStorage.getItem("pendingBooking"); if (s) setItem(JSON.parse(s)); }
  }, [location.state]);

  useEffect(() => { if (user) setForm((f) => ({ ...f, email: f.email || user.email, firstName: f.firstName || user.name?.split(" ")[0] || "" })); }, [user]);

  if (!item) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-heading font-bold text-2xl text-slate-950">Nothing to book yet</h1>
      <p className="text-slate-500 mt-2">Start by searching for a flight, hotel, car or package.</p>
      <Button className="mt-6 rounded-full bg-brand" onClick={() => navigate("/")}>Back to search</Button>
    </div>
  );

  const Icon = ICON[item.type] || Package;
  const taxes = Math.round(item.amount * 0.12);
  const total = item.amount + taxes;

  const confirm = async () => {
    if (!isAuthed) { navigate("/login?next=/checkout"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        type: item.type, title: item.title, amount: total, currency: "GBP",
        display_amount: convert(total), display_currency: code,
        details: item.details, travel_date: item.travel_date || null,
        passenger: { name: `${form.firstName} ${form.lastName}`, email: form.email, phone: form.phone, dob: form.dob, nationality: form.nationality },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment could not be started");
      setSubmitting(false);
    }
  };

  const canProceed = form.firstName && form.lastName && form.email && form.phone;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-8">
        {[[1, "Your details"], [2, "Review & pay"]].map(([n, label]) => (
          <div key={n} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= n ? "bg-brand text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
            <span className={`font-semibold text-sm ${step >= n ? "text-slate-950" : "text-slate-400"}`}>{label}</span>
            {n === 1 && <div className="w-12 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-6"><User className="w-5 h-5 text-brand" /><h2 className="font-heading font-bold text-xl text-slate-950">Traveller details</h2></div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label>First name *</Label><Input data-testid="checkout-firstname" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Last name *</Label><Input data-testid="checkout-lastname" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Email *</Label><Input data-testid="checkout-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Phone *</Label><Input data-testid="checkout-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="mt-1.5" placeholder="e.g. British" /></div>
              </div>
              <Button disabled={!canProceed} onClick={() => setStep(2)} data-testid="checkout-continue" className="mt-7 rounded-full bg-slate-950 hover:bg-slate-800 h-12 px-8 font-heading font-bold gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
                <h2 className="font-heading font-bold text-xl text-slate-950 mb-4">Review traveller</h2>
                <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                  <div><span className="text-slate-400">Name:</span> <span className="font-semibold text-slate-900">{form.firstName} {form.lastName}</span></div>
                  <div><span className="text-slate-400">Email:</span> <span className="font-semibold text-slate-900">{form.email}</span></div>
                  <div><span className="text-slate-400">Phone:</span> <span className="font-semibold text-slate-900">{form.phone}</span></div>
                  {form.nationality && <div><span className="text-slate-400">Nationality:</span> <span className="font-semibold text-slate-900">{form.nationality}</span></div>}
                </div>
                <button onClick={() => setStep(1)} className="text-brand text-sm font-bold mt-3">Edit details</button>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-4"><CreditCard className="w-5 h-5 text-brand" /><h2 className="font-heading font-bold text-xl text-slate-950">Payment</h2></div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
                  <div className="font-bold flex items-center gap-2 text-slate-900"><Lock className="w-4 h-4 text-emerald-600" /> Secure card payment by Stripe</div>
                  <p className="mt-1 text-slate-500">You'll be taken to Stripe's secure checkout to pay by card. Test card: 4242 4242 4242 4242, any future expiry, any CVC. Your booking is confirmed the moment payment succeeds.</p>
                </div>
                <Button onClick={confirm} disabled={submitting} data-testid="confirm-booking" className="w-full mt-5 rounded-full bg-coral hover:bg-coral-hover h-13 py-4 font-heading font-bold text-base gap-2">
                  {submitting ? "Redirecting to secure checkout…" : <><Lock className="w-4 h-4" /> Pay securely · {money(total)}</>}
                </Button>
                {!isAuthed && <p className="text-center text-sm text-slate-500 mt-3">You'll be asked to sign in to complete payment.</p>}
              </div>
            </motion.div>
          )}
        </div>

        <aside>
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {item.details?.image && <img src={item.details.image} alt="" className="w-full h-40 object-cover" />}
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-brand mb-2"><Icon className="w-4 h-4" />{item.type}</div>
              <h3 className="font-heading font-bold text-lg text-slate-950 leading-snug">{item.title}</h3>
              {item.details?.airline && <div className="text-sm text-slate-500 mt-1">{item.details.airline} · {item.details.depart}–{item.details.arrive} · {item.details.duration}</div>}
              {item.details?.nights && <div className="text-sm text-slate-500 mt-1">{item.details.nights} nights · flights + hotel</div>}
              <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Base price</span><span>{money(item.amount)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Taxes & fees</span><span>{money(taxes)}</span></div>
                <div className="flex justify-between font-heading font-black text-lg text-slate-950 pt-2 border-t border-slate-100"><span>Total</span><span>{money(total)}</span></div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-emerald-700"><ShieldCheck className="w-4 h-4" />Price guaranteed · Free cancellation</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
